<template>
  <!-- Mobile / tablet bottom navigation. Hidden on large screens (≥ 992px) via
       the scoped media query; drop <bottom-nav /> at the end of a page's app
       shell (.body flex column) and it pins to the bottom. -->
  <nav class="m-bottomnav">
    <template v-for="item in navItems" :key="item.label">
      <!-- "More" opens the bottom sheet instead of navigating. -->
      <button
        v-if="item.isMore"
        type="button"
        class="m-bottomnav__item"
        :class="{ 'is-active': showMoreSheet || moreIsActive }"
        :aria-expanded="showMoreSheet ? 'true' : 'false'"
        @click="openMoreSheet"
      >
        <span class="m-bottomnav__icon">
          <img :src="item.icon" :alt="item.label" :class="item.iconClass" aria-hidden="true" />
          <span v-if="badgeOf(item)" class="tk-badge m-bottomnav__badge">{{ badgeLabel(badgeOf(item)) }}</span>
        </span>
        <span class="m-bottomnav__label">{{ item.label }}</span>
      </button>
      <router-link
        v-else
        :to="{ name: item.route }"
        class="m-bottomnav__item"
        :class="{ 'is-active': isActive(item) }"
      >
        <span class="m-bottomnav__icon">
          <img
            :src="item.icon"
            :alt="item.label"
            :class="item.iconClass"
            aria-hidden="true"
          />
        </span>
        <span class="m-bottomnav__label">{{ item.label }}</span>
      </router-link>
    </template>
  </nav>

  <!-- "More" bottom sheet — slides up from the bottom edge over the nav. -->
  <transition name="sheet">
    <div
      v-if="showMoreSheet"
      class="m-sheet-overlay"
      @click.self="closeMoreSheet"
    >
      <div class="m-sheet" role="dialog" aria-modal="true" aria-label="More options">
        <span class="m-sheet__handle" @click="closeMoreSheet"></span>
        <div class="m-sheet__header">
          <button type="button" class="m-sheet__close" aria-label="Close" @click="closeMoreSheet">
            <font-awesome-icon icon="fa-solid fa-xmark" />
          </button>
        </div>
        <ul class="m-sheet__list">
          <li v-for="link in moreItems" :key="link.label">
            <router-link
              :to="{ name: link.route }"
              class="m-sheet__item"
              :class="{ 'is-active': isActive(link) }"
              @click="closeMoreSheet"
            >
              <span class="m-sheet__icon">
                <img :src="link.icon" :alt="link.label" aria-hidden="true" />
              </span>
              <span class="m-sheet__label">{{ link.label }}</span>
              <span v-if="badgeOf(link)" class="tk-badge">{{ badgeLabel(badgeOf(link)) }}</span>
            </router-link>
          </li>
          <li class="m-sheet__signout-row">
            <button type="button" class="m-sheet__item m-sheet__item--danger" @click="logout">
              <span class="m-sheet__icon">
                <img :src="logoutIcon" alt="Signout" aria-hidden="true" />
              </span>
              <span class="m-sheet__label">Signout</span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  </transition>
</template>

<script>
import {
  DASHBOARD_ROUTE,
  ANNOUNCEMENTS_N_ROUTE,
  ADD_ANNOUNCEMENT_ROUTE,
  EDIT_ANNOUNCEMENT_ROUTE,
  DUPLICATE_ANNOUNCEMENT_ROUTE,
  SHIUREI_TORAH_N_ROUTE,
  ADD_SHIUREI_TORAH_ROUTE,
  EDIT_SHIUREI_TORAH_ROUTE,
  DUPLICATE_SHIUREI_TORAH_ROUTE,
  DONATIONS_N_ROUTE,
  ADD_DONATION_ROUTE,
  MINYANIM_ROLLS_N_ROUTE,
  ADD_MINYANIM_ROLL_N_ROUTE,
  EDIT_MINYANIM_ROLL_N_ROUTE,
  POSTERS_ROUTE,
  DATE_TIME_BANNER_ROUTE,
  ZMANIM_N_ROUTE,
  ADD_ZMANIM_N_ROUTE,
  EDIT_ZMANIM_N_ROUTE,
  API_KEYS_ROUTE,
  SETTINGS_ROUTE,
  TICKETS_ROUTE,
} from '@/router/routes';

import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { LOGOUT } from '@/store/action_types';

// Reuse the exact icon assets the sidenav uses so the two navs stay in sync.
import dashboardIcon from '@/assets/images/icons/sidenav/dashboard.svg';
import announcementIcon from '@/assets/images/icons/sidenav/Announcement.svg';
import shiureiTorahIcon from '@/assets/images/icons/sidenav/Shiurei_Torah.svg';
import donationIcon from '@/assets/images/icons/sidenav/Donation.svg';
import minyunimIcon from '@/assets/images/icons/sidenav/Minyunim.svg';
import postersIcon from '@/assets/images/icons/sidenav/Posters.svg';
import zmanimIcon from '@/assets/images/icons/sidenav/Zmanim.svg';
import calendarIcon from '@/assets/images/icons/sidenav/Calendar.svg';
import apiIcon from '@/assets/images/icons/sidenav/API.svg';
import settingsIcon from '@/assets/images/icons/sidenav/settings.svg';
import ticketsIcon from '@/assets/images/icons/sidenav/Tickets.svg';
import logoutIcon from '@/assets/images/icons/sidenav/Logout.svg';
import moreIcon from '@/assets/images/icons/sidenav/More.svg';

library.add(faXmark);

export default {
  name: 'BottomNav',
  data() {
    return {
      showMoreSheet: false,
      // `match` lists every route name that should light up this tab, mirroring
      // the sidenav's grouping (e.g. an add/edit page keeps its parent active).
      navItems: [
        { label: 'Home', route: DASHBOARD_ROUTE, icon: dashboardIcon, match: [DASHBOARD_ROUTE] },
        {
          label: 'Announce',
          route: ANNOUNCEMENTS_N_ROUTE,
          icon: announcementIcon,
          match: [ANNOUNCEMENTS_N_ROUTE, ADD_ANNOUNCEMENT_ROUTE, EDIT_ANNOUNCEMENT_ROUTE, DUPLICATE_ANNOUNCEMENT_ROUTE],
        },
        {
          label: 'Donations',
          route: DONATIONS_N_ROUTE,
          icon: donationIcon,
          match: [DONATIONS_N_ROUTE, ADD_DONATION_ROUTE],
        },
        {
          label: 'Minyanim',
          route: MINYANIM_ROLLS_N_ROUTE,
          icon: minyunimIcon,
          iconClass: 'm-bottomnav__img--minyunim',
          match: [MINYANIM_ROLLS_N_ROUTE, ADD_MINYANIM_ROLL_N_ROUTE, EDIT_MINYANIM_ROLL_N_ROUTE],
        },
        { label: 'More', isMore: true, icon: moreIcon, iconClass: 'm-bottomnav__img--more' },
      ],
      // Items shown inside the "More" bottom sheet. Icons mirror the sidenav.
      moreItems: [
        {
          label: 'Shiurei Torah',
          route: SHIUREI_TORAH_N_ROUTE,
          icon: shiureiTorahIcon,
          match: [SHIUREI_TORAH_N_ROUTE, ADD_SHIUREI_TORAH_ROUTE, EDIT_SHIUREI_TORAH_ROUTE, DUPLICATE_SHIUREI_TORAH_ROUTE],
        },
        { label: 'Posters', route: POSTERS_ROUTE, icon: postersIcon, match: [POSTERS_ROUTE] },
        { label: 'Date Time Banner', route: DATE_TIME_BANNER_ROUTE, icon: calendarIcon, match: [DATE_TIME_BANNER_ROUTE] },
        {
          label: 'Zmanim',
          route: ZMANIM_N_ROUTE,
          icon: zmanimIcon,
          match: [ZMANIM_N_ROUTE, ADD_ZMANIM_N_ROUTE, EDIT_ZMANIM_N_ROUTE],
        },
        { label: 'Tickets', route: TICKETS_ROUTE, icon: ticketsIcon, match: [TICKETS_ROUTE], badge: 'tickets' },
        { label: 'API Key', route: API_KEYS_ROUTE, icon: apiIcon, match: [API_KEYS_ROUTE] },
        { label: 'Settings', route: SETTINGS_ROUTE, icon: settingsIcon, match: [SETTINGS_ROUTE] },
      ],
      logoutIcon,
    };
  },
  computed: {
    // The "More" tab lights up while the sheet is open or the active route lives
    // inside the sheet.
    moreIsActive() {
      return this.moreItems.some((item) => item.match.includes(this.$route.name));
    },
    // Tickets with unread replies — the badge on Tickets in the sheet, and
    // on the More tab that opens it.
    unreadTickets() {
      return Number(this.$store.getters.ticketUnread?.data?.tickets) || 0;
    },
  },
  watch: {
    // Always drop the sheet once navigation completes.
    $route() {
      this.showMoreSheet = false;
    },
  },
  methods: {
    // Match on the route name rather than relying on `router-link-active`:
    // the Home tab points at "/", whose path is a prefix of every route, so
    // the built-in active class would mark Home active everywhere.
    isActive(item) {
      return (item.match || []).includes(this.$route.name);
    },
    badgeOf(item) {
      return item.isMore || item.badge === 'tickets' ? this.unreadTickets : 0;
    },
    badgeLabel(count) {
      return count > 99 ? '99+' : String(count);
    },
    openMoreSheet() {
      this.showMoreSheet = true;
    },
    closeMoreSheet() {
      this.showMoreSheet = false;
    },
    logout() {
      this.closeMoreSheet();
      this.$store.dispatch(LOGOUT, { recordSignOut: true }).then(() => {
        this.$router.go();
      });
    },
  },
};
</script>

<style scoped>
.m-bottomnav {
  display: none;
}

@media (max-width: 991.98px) {
  /* Pinned to the real viewport bottom with position:fixed so it is NEVER tied
     to the app-shell's `zoom: 0.9` counter-scaled height (`calc(100dvh / 0.9)`).
     On browsers that honor `zoom` differently than the build/dev environment,
     that counter-scale overshoots the viewport and `overflow:hidden` clipped a
     static bottom nav off-screen — this makes the bar always visible.
     left/right/bottom:0 (no vw/vh units) pin to the viewport edges under every
     zoom behavior. Pages reserve matching space (see index.css). */
  .m-bottomnav {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 900;
    display: flex;
    flex: none;
    align-items: stretch;
    justify-content: space-around;
    min-height: 64px;
    padding: 6px 4px calc(6px + env(safe-area-inset-bottom, 0px));
    background: var(--color-bg-canvas);
    border-top: 1px solid var(--color-border);
    box-shadow: 0 -2px 12px rgba(17, 24, 39, 0.05);
  }

  .m-bottomnav__item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    padding: 0;
    background: transparent;
    border: none;
    font-family: inherit;
    text-decoration: none !important;
    color: var(--color-text-muted);
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: color .15s ease;
  }

  .m-bottomnav__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    font-size: 1.25rem; /* sizes the Font Awesome glyph */
  }

  .m-bottomnav__icon img {
    width: 21px;
    height: 21px;
    object-fit: contain;
    opacity: 0.55;
    transition: opacity .15s ease;
  }

  /* The unread count sits on the tab's icon. */
  .m-bottomnav__icon {
    position: relative;
  }

  .m-bottomnav__badge {
    position: absolute;
    top: -6px;
    left: calc(50% + 5px);
    box-shadow: 0 0 0 2px var(--color-bg-canvas);
  }

  .m-bottomnav__icon img.m-bottomnav__img--minyunim {
    width: 24px;
    height: 24px;
  }

  .m-bottomnav__icon img.m-bottomnav__img--more {
    width: 26px;
    height: 26px;
    opacity: 0.82;
  }

  .m-bottomnav__label {
    font-size: 0.66rem;
    font-weight: 500;
    line-height: 1;
  }

  .m-bottomnav__item:hover {
    color: var(--color-text-secondary);
  }

  .m-bottomnav__item:hover .m-bottomnav__icon img {
    opacity: 0.85;
  }

  .m-bottomnav__item.is-active {
    color: var(--color-primary);
  }

  .m-bottomnav__item.is-active .m-bottomnav__icon img {
    opacity: 1;
  }
}

/* =========================================================================
   "MORE" BOTTOM SHEET
   -------------------------------------------------------------------------
   A full-screen fixed overlay with the sheet anchored to the bottom edge.
   The sheet only ever shows on ≤991.98px, where the app `zoom` is now OFF
   (see index.css) — so it fills the real viewport at 100% with no /0.9
   counter-scale. (Previously the /0.9 over-inflated it on real devices,
   pushing the sheet's bottom — and the Signout row — off-screen.)
   ========================================================================= */
.m-sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  background: rgba(17, 24, 39, 0.45);
}

.m-sheet {
  width: 100%;
  max-height: calc(100vh - 12px);
  max-height: calc(100dvh - 12px);
  display: flex;
  flex-direction: column;
  background: var(--color-bg-canvas);
  border-top-left-radius: 22px;
  border-top-right-radius: 22px;
  box-shadow: 0 -10px 34px rgba(17, 24, 39, 0.20);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  overflow: hidden;
}

.m-sheet__handle {
  width: 42px;
  height: 4px;
  margin: 10px auto 2px;
  border-radius: 999px;
  background: var(--color-border-strong, #d1d5db);
  cursor: pointer;
}

.m-sheet__header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 10px 18px 14px;
  border-bottom: 1px solid var(--color-divider);
}

.m-sheet__close {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-size: 1rem;
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease;
}

.m-sheet__close:hover {
  background: var(--color-border);
  color: var(--color-text-primary);
}

.m-sheet__list {
  flex: 1 1 auto;
  min-height: 0;
  list-style: none;
  margin: 0;
  padding: 10px 12px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.m-sheet__list li {
  list-style: none;
}

.m-sheet__item {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 12px;
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-text-primary) !important;
  font: inherit;
  text-align: left;
  text-decoration: none !important;
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease;
}

.m-sheet__item:hover,
.m-sheet__item.is-active {
  background: var(--color-primary-soft);
  color: var(--color-primary-hover) !important;
}

.m-sheet__signout-row {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--color-divider);
}

.m-sheet__item--danger,
.m-sheet__item--danger:hover {
  color: #FF383C !important;
}

.m-sheet__icon {
  width: 24px;
  flex: 0 0 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
  color: var(--color-text-secondary);
}

.m-sheet__item.is-active .m-sheet__icon {
  color: var(--color-primary);
}

.m-sheet__icon img {
  width: 21px;
  height: 21px;
  object-fit: contain;
}

.m-sheet__label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.95rem;
  font-weight: 500;
}

/* --- Slide-up + fade transition --- */
.sheet-enter-active,
.sheet-leave-active {
  transition: opacity .25s ease;
}

.sheet-enter-from,
.sheet-leave-to {
  opacity: 0;
}

.sheet-enter-active .m-sheet,
.sheet-leave-active .m-sheet {
  transition: transform .3s cubic-bezier(.32, .72, 0, 1);
}

.sheet-enter-from .m-sheet,
.sheet-leave-to .m-sheet {
  transform: translateY(100%);
}

/* On large screens the sheet is never opened (the nav is hidden); guard against
   it lingering visible if the viewport is resized up while it's open. */
@media (min-width: 992px) {
  .m-sheet-overlay {
    display: none !important;
  }
}
</style>
